import groupBy from "lodash/groupBy";

import { EntityWithUrl } from "@/utils/sos/utilities";
import { Adjustment, AdjustmentLineItem } from "./adjustments";
import { Build, BuildLineItem } from "./builds";
import { ExistingInventoryQuantity } from "./existing-inventory-quantity";
import { Item } from "./items";
import { Bin } from "./sales-order-rack";
import { Shipment, ShipmentLineItem } from "./shipments";
import { Transfer, TransferLineItem } from "./transfers";
import { ItemReceipt } from "./item-receipts";

export interface ExistingInventoryForItem {
  item: Item;
  quantity: ExistingInventoryQuantity;
  sources: ExistingInventorySource[];
}

export type ExistingInventorySource = PartialExistingInventorySource & {
  balance: number;
};

type PartialExistingInventorySource =
  | BaseExistingInventorySource<"Transfer", "transfer", Transfer, TransferLineItem>
  | BaseExistingInventorySource<"Adjustment", "adjustment", Adjustment, AdjustmentLineItem>
  | BaseExistingInventorySource<"Build Input", "build", Build, BuildLineItem>
  | BaseExistingInventorySource<"Build Output", "build", Build, BuildLineItem>
  | BaseExistingInventorySource<"Shipment", "shipment", Shipment, ShipmentLineItem>;

interface BaseExistingInventorySource<TType extends string, TRecordType extends EntityWithUrl, TRecord, TLine> {
  type: TType;
  sourceRecordType: TRecordType;
  sourceRecord: TRecord;
  sourceLine: TLine;
}

export function getExistingInventoryBreakdown(
  jobId: number,
  salesOrderRack: Bin,
  transfers: Transfer[],
  adjustments: Adjustment[],
  builds: Build[],
  shipments: Shipment[],
  itemReceipts: ItemReceipt[],
) {
  // Collect all the relevant record line items into a single generic collection
  // prettier-ignore
  const sourceLines = [
    ...transfers.flatMap(transfer => transfer.salesOrderRackLines.map<PartialExistingInventorySource>((sourceLine) =>
      ({ type: "Transfer", sourceRecordType: 'transfer', sourceRecord: transfer, sourceLine }))),
    ...adjustments.flatMap(adjustment => adjustment.salesOrderRackLines.map<PartialExistingInventorySource>((sourceLine) =>
      ({ type: "Adjustment", sourceRecordType: 'adjustment', sourceRecord: adjustment, sourceLine, }))),
    ...builds.flatMap((build) => build.inputs.map<PartialExistingInventorySource>((sourceLine) =>
      ({ type: "Build Input", sourceRecordType: 'build', sourceRecord: build, sourceLine }))),
    ...builds.flatMap((build) => build.outputs.map<PartialExistingInventorySource>((sourceLine) =>
      ({ type: "Build Output", sourceRecordType: 'build', sourceRecord: build, sourceLine }))),
    ...shipments.flatMap((shipment) => shipment.allLines.map<PartialExistingInventorySource>((sourceLine) =>
      ({ type: "Shipment", sourceRecordType: 'shipment', sourceRecord: shipment, sourceLine }))),
  ];

  // For non-inventory items, create a map of item id, to the number of that item received for this job
  // We will first pull from job-specific item receipts, and then any remaining required quantity will be assumed to be
  // pulled from existing inventory
  const nonInventoryLines = itemReceipts
    .flatMap((receipt) => receipt.jobLines)
    .filter((line) => line.item.type === "Non-inventory Item")
    .map((line) => line)
    .filter((line) => line.baseQuantity);
  const nonInventoryPurchaseQuantities = new Map(
    Object.entries(groupBy(nonInventoryLines, (line) => line.item.id)).map(([itemId, lines]) => [
      Number(itemId),
      lines.reduce((sum, line) => sum + line.baseQuantity, 0),
    ]),
  );

  // Get the quantity of existing inventory used for each collected line item
  const rackId = salesOrderRack.id;
  const sourceRecords = sourceLines
    .map<ExistingInventorySource>((line) => {
      const balance = getExistingInventoryBalanceForSourceLine(jobId, rackId, line, nonInventoryPurchaseQuantities);
      return { ...line, balance };
    })
    .filter((x) => x.balance !== 0);

  // Group the line items by item then collapse them so we end up with a single record per item
  // Sort by quantity so that positive and negative values are separated
  const recordsByItem = Map.groupBy(sourceRecords, (record) => record.sourceLine.item.id);
  return Array.from(recordsByItem.values())
    .map<ExistingInventoryForItem>((records) => {
      const item = records[0]!.sourceLine.item;
      const description = records
        .map((x) => x.sourceLine.description || "")
        .reduce((a, b) => (a.length > b.length ? a : b), "");
      const quantity = records.reduce(
        (sum, { balance }) => sum.add(new ExistingInventoryQuantity({ balance })),
        ExistingInventoryQuantity.ZERO,
      );
      return { item, description, quantity, sources: records };
    })
    .sort((a, b) => a.item.type.localeCompare(b.item.type) || a.item.name.localeCompare(b.item.name));
}

const getExistingInventoryBalanceForSourceLine = (
  jobId: number,
  rackId: number,
  { type, sourceLine: line }: PartialExistingInventorySource,
  nonInventoryPurchaseQuantities: Map<number, number>,
): number => {
  // For transfers, the quantity of existing inventory used for a given line item is:
  // - 0 if the source and destination bin are both the sales order rack
  // - the positive transfer quantity if the sales order rack is the destination bin
  // - The negative transfer quantity if the sales order rack is the source bin
  if (type === "Transfer") {
    const sameBin = line.fromBin!.id === line.toBin!.id;
    return (
      sameBin ? 0
      : line.fromBin!.id === rackId ? -line.baseQuantity
      : line.baseQuantity
    );
  }

  // For adjustments, the quantity difference on the line item can be used to see how much existing inventory was used
  if (type === "Adjustment") return line.baseQuantityDiff;

  // If this is a non-inventory item, try to allocate some of the quantity from purchases first
  // Note that we also need to check no bin is assigned because the build record could have been created before the item
  // was changed to non-inventory, in which case we treat it as a regular inventory item
  const toInventory = type === "Build Output";
  if (!line.bin && line.item.type === "Non-inventory Item") {
    // Allocate purchased quantity first
    const availablePurchasedQuantity = nonInventoryPurchaseQuantities.get(line.item.id) ?? 0;
    const purchasedQuantityUsedForLine = Math.min(availablePurchasedQuantity, line.baseQuantity);
    nonInventoryPurchaseQuantities.set(line.item.id, availablePurchasedQuantity - purchasedQuantityUsedForLine);

    // The remaining quantity is taken from existing inventory
    const inventoryQuantity = line.baseQuantity - purchasedQuantityUsedForLine;
    return toInventory ? -inventoryQuantity : inventoryQuantity;
  }

  // For shipments, the quantity of existing inventory used for a given line is:
  // - 0 if there is no source bin (i.e. non-inventory items)
  // - 0 if job id and source bin both DO match this order (i.e. normal shipment for this SO)
  // - 0 if job id and source bin both DO NOT match this order (i.e. normal shipment for a different SO)
  // - The positive quantity on the line item if job matches, but the source bin does not (i.e. we took something from stock and shipped it for this job)
  // - The negative quantity on the line item if the bin matches, but the job does not (i.e. we took something from this SO rack and shipped it for a different job)
  if (type === "Shipment") {
    if (!line.bin) return 0;
    const binMatches = line.bin.id === rackId;
    const jobMatches = line.job?.id === jobId;
    if (binMatches && jobMatches) return 0;
    if (!binMatches && !jobMatches) return 0;
    return jobMatches ? line.baseQuantity : -line.baseQuantity;
  }

  // For build inputs/outputs, the quantity of existing inventory used for a given line is:
  //
  // - 0 if there is no source/destination bin (i.e. non-inventory items)
  // - 0 if the source/destination bin is the sales order rack
  // - the positive quantity for builds inputs
  // - the negative quantity for build outputs
  //
  // NOTE: This does not handle the case where a build input is taken from this SO rack and used as a build input for a
  // different job. This does not generally happen because we ensure items are available on the SO rack before work
  // orders are created.
  return (
    !line.bin ? 0
    : line.bin.id === rackId ? 0
    : type === "Build Input" ? line.baseQuantity
    : -line.baseQuantity
  );
};

/**
 * This class takes an initial amount of existing inventory stock to allocate and exposes a `tryAllocate` method that
 * can be used to allocate existing inventory for a given item ID and quantity.
 */
export class ExistingInventoryAllocator {
  private readonly unallocatedStockByItemId: Map<number, number>;

  constructor(existingInventory: ExistingInventoryForItem[]) {
    this.unallocatedStockByItemId = new Map(existingInventory.map((x) => [x.item.id, x.quantity.balance]));
  }

  /**
   * Attempts to use existing inventory for a given item ID and up to the specified quantity. The map of existing
   * inventory quantities is updated to reflect the amount used. The value returned is the amount used from existing
   * inventory.
   */
  public tryAllocate(itemId: number, quantity: number) {
    if (!this.unallocatedStockByItemId.has(itemId)) return 0;
    const existingInventory = this.unallocatedStockByItemId.get(itemId)!;
    const usedInventory = Math.min(existingInventory, quantity);
    this.unallocatedStockByItemId.set(itemId, existingInventory - usedInventory);
    return usedInventory;
  }
}
